"use client";

import React from "react";
import { Box } from "@mui/material";

export type CardPillTone = "success" | "warning";
export type CardPillPlacement = "top-right" | "bottom-left";

export interface CardPillProps {
  label: string;
  tone: CardPillTone;
  placement: CardPillPlacement;
  icon?: React.ReactNode;
}

const PLACEMENT_SX: Record<CardPillPlacement, Record<string, number>> = {
  "top-right": { top: 8, right: 8 },
  "bottom-left": { bottom: 8, left: 8 },
};

/**
 * A small label sitting on top of a product photo.
 *
 * Deliberately opaque rather than translucent: this lands on an arbitrary
 * customer-facing product photo and has to stay readable over all of them.
 *
 * `pointerEvents: none` matters — the pill overlays the photo's <button>, and
 * without it the pill would swallow clicks that are meant to open the modal.
 */
const CardPill: React.FC<CardPillProps> = ({ label, tone, placement, icon }) => (
  <Box
    sx={(t) => ({
      position: "absolute",
      ...PLACEMENT_SX[placement],
      zIndex: 1,
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      gap: 0.375,
      // Never let a long translation run past the frame.
      maxWidth: "calc(100% - 16px)",
      px: 0.75,
      py: 0.25,
      borderRadius: 999,
      backgroundColor: t.palette[tone].main,
      color: t.palette[tone].contrastText,
      fontSize: t.typography.caption.fontSize,
      lineHeight: 1.4,
      fontWeight: 600,
      "& > svg": { fontSize: 14, flexShrink: 0 },
    })}
  >
    {icon}
    <Box
      component="span"
      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      {label}
    </Box>
  </Box>
);

export default CardPill;
